import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivate } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import * as fromUserProfile from '@shared/user-profile.reducer';
import { CommonService } from '@modules/shared/services/common.service';
import { AuthService } from '@core/auth.service';
import { PlatformService } from '@core/services/platform.service';

/**
 * Allows a route only when the current viewer owns the site context.
 * - Not a white-label site: always allowed.
 * - White-label site: allowed only for the WLS owner; any other creator or
 *   fan is treated as a non-owner and redirected home.
 */
@Injectable({
  providedIn: 'root'
})
export class WlsOwnerGuard implements CanActivate {
  private deferredCheckActive = false;
  private readonly isBrowser: boolean;

  constructor(
    private readonly cs: CommonService,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    private readonly authService: AuthService,
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly platformService: PlatformService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  canActivate(): boolean {
    if (this.platformService.isPrerendered) {
      return true;
    }

    if (!this.authService.isWhiteLabelWebsite()) {
      return true;
    }

    const userProfile = this.cs.getLoggedInUser();

    if (!userProfile || Array.isArray(userProfile)) {
      this.deferredOwnerCheck();
      return true;
    }

    return this.evaluateOwnership(userProfile);
  }

  private evaluateOwnership(userProfile: any): boolean {
    if (this.isWlsOwner(userProfile)) {
      return true;
    }
    this.cs.navigateToWhiteLabelHome();
    return false;
  }

  private deferredOwnerCheck(): void {
    if (!this.isBrowser || this.deferredCheckActive) {
      return;
    }
    this.deferredCheckActive = true;

    this.store
      .select(fromUserProfile.getUserProfileItems)
      .pipe(
        filter((profile: any) => !!profile && !Array.isArray(profile)),
        take(1)
      )
      .subscribe((userProfile: any) => {
        this.deferredCheckActive = false;
        if (!this.isWlsOwner(userProfile)) {
          this.cs.navigateToWhiteLabelHome();
        }
      });
  }

  private isWlsOwner(userProfile: any): boolean {
    if (userProfile?.is_host_owner) {
      return true;
    }
    const wlsOwnerId = this.cs.getWhiteLabelConfiguration()?.user?.gsi4pk;
    return !!wlsOwnerId && userProfile?.gsi4pk === wlsOwnerId;
  }
}
